import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";

type LoadState = "idle" | "loading" | "error";

const WalkerStripeRefreshPage: React.FC = () => {
  const hasStartedRef = useRef(false);
  const [state, setState] = useState<LoadState>("idle");
  const [error, setError] = useState("");

  useEffect(() => {
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    async function run() {
      try {
        setState("loading");
        setError("");

        const me = await walkiesClient.getWalkerMe();

        const walkerProfileId =
          (me as any)?.walkerProfileId ??
          (me as any)?.profileId ??
          (me as any)?.walkerId ??
          0;

        const walkerEmail = me?.email?.trim() ?? "";

        if (!walkerProfileId || !walkerEmail) {
          throw new Error("Your walker session could not be resolved.");
        }

        const result = await walkiesClient.createWalkerStripeConnectLink({
          walkerProfileId,
          walkerEmail,
        });

        if (!result.url?.trim()) {
          throw new Error("Stripe onboarding link was not returned.");
        }

        window.location.href = result.url;
      } catch (err) {
        setState("error");
        setError(
          err instanceof Error
            ? err.message
            : "Unable to refresh Stripe onboarding."
        );
      }
    }

    void run();
  }, []);

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          {state === "loading" || state === "idle" ? (
            <>
              <div style={styles.badge}>Stripe onboarding</div>
              <h1 style={styles.title}>Refreshing your Stripe setup…</h1>
              <p style={styles.text}>
                Please wait while we prepare a fresh onboarding link.
              </p>
            </>
          ) : (
            <>
              <div style={styles.badge}>Stripe onboarding</div>
              <h1 style={styles.title}>We couldn’t refresh Stripe onboarding</h1>
              <p style={styles.text}>
                {error || "Something went wrong while preparing Stripe setup."}
              </p>

              <div style={styles.actions}>
                <Link to="/walker/dashboard" style={styles.primaryLinkButton}>
                  Back to dashboard
                </Link>

                <Link to="/walker/messages" style={styles.secondaryLinkButton}>
                  Go to messages
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242,138,22,0.16), transparent 28%), linear-gradient(180deg,#2f4f46 0%,#213a33 100%)",
    padding: "32px 16px 64px",
    color: "#fff",
  },
  container: {
    maxWidth: 760,
    margin: "0 auto",
  },
  card: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 28,
    boxShadow: "0 18px 48px rgba(0,0,0,0.18)",
  },
  badge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(47,79,70,0.08)",
    fontWeight: 800,
    marginBottom: 16,
  },
  title: {
    margin: "0 0 12px",
    fontSize: 38,
    fontWeight: 900,
    color: "#213a33",
  },
  text: {
    margin: 0,
    fontSize: 16,
    lineHeight: 1.7,
    color: "rgba(24, 32, 29, 0.8)",
  },
  actions: {
    marginTop: 24,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
  },
  primaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "12px 18px",
    background: "#f28a16",
    color: "#fff",
    fontWeight: 800,
    textDecoration: "none",
  },
  secondaryLinkButton: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 16,
    padding: "12px 18px",
    background: "#213a33",
    color: "#fff",
    fontWeight: 800,
    textDecoration: "none",
  },
};

export default WalkerStripeRefreshPage;